import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown } from "lucide-react";
import { USDC_ICON } from "./bridgeConstants";

/** Format balance: >= 1 use 2 decimals, < 1 show enough to see significant digits */
function smartFormat(val) {
  if (!val || val === 0) return "0.00";
  if (val >= 1) return val.toFixed(2);
  let d = 2;
  while (d < 10 && Number(val.toFixed(d)) === 0) d++;
  return val.toFixed(Math.min(d + 1, 10));
}

/**
 * USDC icon with small chain badge overlay (bottom-left).
 */
function UsdcWithChainBadge({ chainIcon, chainName, size = 28, badgeSize = 14 }) {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <img
        src={USDC_ICON}
        alt="USDC"
        className="rounded-full"
        style={{ width: size, height: size }}
      />
      <img
        src={chainIcon}
        alt={chainName}
        className="absolute rounded-full border-2 border-white"
        style={{
          width: badgeSize,
          height: badgeSize,
          bottom: -2,
          right: -2,
        }}
      />
    </div>
  );
}

/**
 * Chain selector dropdown.
 * Shows USDC icon with chain badge, matching the Sai bridge style.
 */
export default function ChainSelector({
  chains,
  selected,
  onSelect,
  balances = {},
  loadingBalances = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Selected chain button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-[10px] cursor-pointer hover:bg-[#f0ecf5] rounded-[8px] px-2 py-1.5 transition-colors"
      >
        <UsdcWithChainBadge
          chainIcon={selected.icon}
          chainName={selected.name}
          size={32}
          badgeSize={16}
        />
        <div className="text-left">
          <div className="text-[14px] font-semibold text-[#111]">USDC</div>
          <div className="text-[11px] text-[#888]">{selected.name}</div>
        </div>
        <ChevronDown
          size={16}
          className={`text-[#888] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-50 w-[280px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.08)] shadow-[5px_4px_40px_0px_rgba(0,0,0,0.12)] overflow-hidden">
          {chains.map((chain) => {
            const chainKey = chain.key || chain.id;
            const isSelected = chainKey === (selected.key || selected.id);
            const bal = balances[chainKey];

            return (
              <button
                key={chainKey}
                onClick={() => {
                  onSelect(chain);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                  isSelected ? "bg-[#f8f4fc]" : "hover:bg-[#faf8fc]"
                }`}
              >
                <div className="flex items-center gap-[10px]">
                  <img
                    src={chain.icon}
                    alt={chain.name}
                    className="w-[22px] h-[22px] rounded-full"
                  />
                  <span className="text-[13px] font-medium text-[#111]">
                    {chain.name}
                  </span>
                </div>

                <div className="flex items-center gap-[8px]">
                  <span className="text-[12px] text-[#888]">
                    {loadingBalances
                      ? "..."
                      : `${smartFormat(parseFloat(bal || "0"))} USDC`}
                  </span>
                  {isSelected && (
                    <Check size={14} className="text-[#5b1dee]" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
